import { FC, useCallback, useState } from 'react';
import { Button, Flex, SimpleGrid } from '@chakra-ui/react';

import { OrderAttributeDefinition } from '@app/api/models/order';
import { RequestPayload } from '@app/api/types';
import { OrderAttributeDefinitionListPayload } from '@app/api/orders';
import { Dialog, SelectField, SwitchField, TextField } from '@app/components/common';
import NumberField from '@app/components/common/number-field';
import { QueryTable } from '@app/components/common/table';
import { useQueryTable } from '@app/components/common/table/query-table';
import { useAlert, useApi, useFetchData } from '@app/hooks';

import { useColumns } from './columns';

const DATA_TYPES: EntityBase[] = [
  { id: 'string', name: 'string' },
  { id: 'number', name: 'number' },
  { id: 'boolean', name: 'boolean' },
  { id: 'date', name: 'date' },
  { id: 'select', name: 'select' },
];

type AttributeDraft = Partial<OrderAttributeDefinition> & {
  optionsText?: string;
};

const AttributeDialog: FC<{
  open: boolean;
  initial?: AttributeDraft;
  onClose: () => void;
}> = ({ open, initial, onClose }) => {
  const api = useApi();
  const { error, success } = useAlert();
  const { refetch } = useQueryTable();
  const isEdit = !!initial?.id;

  const [draft, setDraft] = useState<AttributeDraft>({
    is_required: false,
    is_multiselect: false,
    data_type: 'string',
    ...initial,
    optionsText: Array.isArray(initial?.options)
      ? (initial.options as string[]).join('\n')
      : '',
  });
  const [loading, setLoading] = useState(false);

  const patch = (value: AttributeDraft) => setDraft((prev) => ({ ...prev, ...value }));

  const parseOptions = () => {
    if (draft.data_type !== 'select') {
      return null;
    }
    const lines = (draft.optionsText || '')
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);
    return lines.length ? lines : null;
  };

  const onSave = async () => {
    if (!draft.name) {
      error('Введите название');
      return;
    }
    if (!isEdit && !draft.code) {
      error('Введите код');
      return;
    }
    if (!draft.data_type) {
      error('Выберите тип данных');
      return;
    }

    setLoading(true);
    try {
      if (isEdit && draft.id) {
        const response = await api.orders.updateAttribute(draft.id, {
          name: draft.name,
          data_type: draft.data_type,
          options: parseOptions(),
        });
        if (response?.code === 200) {
          success('Атрибут обновлён');
          refetch();
          onClose();
        }
      } else {
        const response = await api.orders.createAttribute({
          name: draft.name,
          code: draft.code,
          data_type: draft.data_type,
          distributor_id: draft.distributor_id ?? null,
          options: parseOptions(),
          is_required: draft.is_required ?? false,
          is_multiselect: draft.is_multiselect ?? false,
          line_numbers: draft.line_numbers ?? null,
          max_length: draft.max_length ?? null,
          reg_exp: draft.reg_exp || null,
          mask: draft.mask,
        });
        if (response?.code === 200) {
          success('Атрибут создан');
          refetch();
          onClose();
        }
      }
    } catch (e) {
      error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редактирование атрибута' : 'Создание атрибута'}
      width={640}
      footerless
    >
      <Flex gap={3} flexDirection="column">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <TextField
            id="name"
            label="Название"
            required
            value={draft.name || ''}
            onChange={(value) => patch({ name: value })}
          />
          <TextField
            id="code"
            label="Код"
            required={!isEdit}
            disabled={isEdit}
            value={draft.code || ''}
            onChange={(value) => patch({ code: value })}
          />
          <SelectField
            id="data_type"
            label="Тип данных"
            required
            options={DATA_TYPES}
            value={
              draft.data_type
                ? { id: draft.data_type, name: draft.data_type }
                : undefined
            }
            onChange={(option) =>
              patch({ data_type: option ? String(option.id) : undefined })
            }
          />
          {!isEdit && (
            <NumberField
              id="distributor_id"
              label="Дистрибьютор ID"
              value={draft.distributor_id ?? 0}
              onChange={(value) =>
                patch({ distributor_id: value || null })
              }
            />
          )}
          {!isEdit && (
            <NumberField
              id="max_length"
              label="Макс. длина"
              value={draft.max_length ?? 0}
              onChange={(value) => patch({ max_length: value || null })}
            />
          )}
          {!isEdit && (
            <TextField
              id="mask"
              label="Маска"
              value={draft.mask || ''}
              onChange={(value) => patch({ mask: value })}
            />
          )}
        </SimpleGrid>
        {draft.data_type === 'select' && (
          <TextField
            id="options"
            label="Варианты (по одному на строку)"
            multiline
            rows={4}
            value={draft.optionsText || ''}
            onChange={(value) => patch({ optionsText: value })}
          />
        )}
        {!isEdit && (
          <Flex gap={6} wrap="wrap">
            <SwitchField
              id="is_required"
              label="Обязательный"
              value={draft.is_required}
              onChange={(value) => patch({ is_required: value })}
            />
            <SwitchField
              id="is_multiselect"
              label="Мультивыбор"
              value={draft.is_multiselect}
              onChange={(value) => patch({ is_multiselect: value })}
            />
          </Flex>
        )}
        <Flex justifyContent="flex-end" gap={2}>
          <Button variant="outline" colorScheme="gray" onClick={onClose} isDisabled={loading}>
            Отмена
          </Button>
          <Button variant="solid" colorScheme="brand" onClick={onSave} isLoading={loading}>
            Сохранить
          </Button>
        </Flex>
      </Flex>
    </Dialog>
  );
};

export const OrderAttributesTable: FC = () => {
  const api = useApi();
  const [selected, setSelected] = useState<AttributeDraft>();
  const [open, setOpen] = useState(false);

  const onEdit = useCallback((attribute: OrderAttributeDefinition) => {
    setSelected(attribute);
    setOpen(true);
  }, []);

  const columns = useColumns({ onEdit });

  const fetchAttributes = useCallback(
    (payload: RequestPayload) =>
      api.orders.getAttributes({
        limit: payload.limit,
        offset: payload.offset,
        sort: payload.sort,
        filter: payload.filter as OrderAttributeDefinitionListPayload['filter'],
      }),
    [api]
  );

  const [fetchData] = useFetchData<OrderAttributeDefinition>({ method: fetchAttributes });

  return (
    <QueryTable<OrderAttributeDefinition>
      title="Атрибуты заказов"
      columns={columns}
      fetchData={fetchData}
      onCreateClick={() => {
        setSelected({ is_required: false, is_multiselect: false, data_type: 'string' });
        setOpen(true);
      }}
    >
      {open && (
        <AttributeDialog
          key={selected?.id || 'create'}
          open={open}
          initial={selected}
          onClose={() => setOpen(false)}
        />
      )}
    </QueryTable>
  );
};

export default OrderAttributesTable;
